import { Edit, Trash2 } from "lucide-react";
import { Modal } from "../Modal";
import type { Fee } from "../../services/financialService";
import { feeCategoryLabel, feeTermsLabel } from "./feeCatalogue";

const AUDIENCE = {
  all: "Day and boarding students",
  day: "Day students only",
  boarding: "Boarding students only",
} as const;

/** Everything about one catalogue item, including a description too long for its table row. */
export function FeeDetailsModal({
  fee,
  incomeAccount,
  onClose,
  onEdit,
  onDelete,
}: {
  fee: Fee | null;
  incomeAccount: (fee: Fee) => string;
  onClose: () => void;
  /** Omitted for users who can't change the catalogue. */
  onEdit?: (fee: Fee) => void;
  onDelete?: (fee: Fee) => void;
}) {
  if (!fee) return null;

  const details: [string, string, string?][] = [
    ["Category", feeCategoryLabel(fee.fee_type)],
    ["Who pays", AUDIENCE[fee.applies_to ?? "all"]],
    [
      "School transport",
      fee.transport_only ? "Billed only to students who use school transport" : "Billed regardless of transport",
    ],
    [
      "Payment terms",
      feeTermsLabel(fee),
      fee.allows_partial_payment
        ? "Parents may pay this fee in installments."
        : "Must be paid in full in one payment.",
    ],
    ["Income account", incomeAccount(fee)],
    [
      "Fee structures",
      fee.is_active ? "Can be added to new fee structures" : "Not offered on new fee structures",
    ],
  ];

  return (
    <Modal
      isOpen
      size="xl"
      title={fee.name}
      onClose={onClose}
      footer={
        <>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(fee)}
              className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-outline">
            Close
          </button>
          {onEdit && (
            <button type="button" onClick={() => onEdit(fee)} className="btn-primary">
              <Edit className="mr-1.5 h-4 w-4" /> Edit fee
            </button>
          )}
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Amount</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">
              UGX {Number(fee.amount).toLocaleString()}
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              fee.is_active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${fee.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
            {fee.is_active ? "Active" : "Inactive"}
          </span>
        </div>

        <section aria-labelledby="fee-description-heading">
          <h4 id="fee-description-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Description
          </h4>
          {fee.description?.trim() ? (
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{fee.description}</p>
          ) : (
            <p className="mt-1.5 text-sm text-gray-400">No description provided.</p>
          )}
        </section>

        <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200">
          {details.map(([label, value, note]) => (
            <div key={label} className="grid gap-1 px-4 py-2.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-4">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="text-sm font-medium text-gray-900">
                {value}
                {note && <span className="mt-0.5 block text-xs font-normal text-gray-500">{note}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </Modal>
  );
}
